import TreeDataController from './TreeDataController';
import { TDataNode, TId } from './types';

export const getLastVisibleChild: <
  NodeProps extends Record<string, unknown> = Record<string, unknown>,
  NodeType extends TDataNode<NodeProps> = TDataNode<NodeProps>,
>(
  handler: TreeDataController<NodeProps, NodeType>,
  id: TId,
  avoidFiltered?: boolean,
) => TId | null = (handler, id, avoidFiltered) => {
  const nodeProps = handler.propsStore.getFieldProps(id);
  if (
    (id !== 'root' && (nodeProps.isDisabled || !nodeProps.isExpanded)) ||
    nodeProps.children.length === 0
  )
    return null;

  for (let i = nodeProps.children.length - 1; i >= 0; i--) {
    const currentId = nodeProps.children[i];
    const currentProps = handler.propsStore.getFieldProps(currentId);
    if (!avoidFiltered || !currentProps.isFiltered) {
      const lastChildrenLastChildren = getLastVisibleChild(
        handler,
        currentId,
        avoidFiltered,
      );
      return lastChildrenLastChildren !== null
        ? lastChildrenLastChildren
        : currentId;
    }
  }
  return null;
};

export const getPreviousChild: <
  NodeProps extends Record<string, unknown> = Record<string, unknown>,
  NodeType extends TDataNode<NodeProps> = TDataNode<NodeProps>,
>(
  handler: TreeDataController<NodeProps, NodeType>,
  id: TId,
  avoidFiltered?: boolean,
) => TId | null = (handler, id, avoidFiltered) => {
  const nodeProps = handler.propsStore.getFieldProps(id);
  const fatherProps = handler.propsStore.getFieldProps(nodeProps.parentId);
  let childPosition =
    fatherProps.children?.findIndex((current) => current === nodeProps.id) ??
    -1;

  while (childPosition > 0) {
    const prevSibling = handler.propsStore.getFieldProps(
      fatherProps.children[childPosition - 1],
    );
    const prevSiblingLastChild = getLastVisibleChild(
      handler,
      prevSibling.id,
      avoidFiltered,
    );
    if (prevSiblingLastChild !== null) return prevSiblingLastChild;
    if (!avoidFiltered || !prevSibling.isFiltered) return prevSibling.id;

    childPosition--;
  }
  if (nodeProps.parentId === 'root') return null;
  return nodeProps.parentId;
};

export const getFirstNonFilteredChild: <
  NodeProps extends Record<string, unknown> = Record<string, unknown>,
  NodeType extends TDataNode<NodeProps> = TDataNode<NodeProps>,
>(
  handler: TreeDataController<NodeProps, NodeType>,
  id: TId,
) => null | TId = (handler, id) => {
  const nodeProps = handler.propsStore.getFieldProps(id);

  for (const child of nodeProps.children) {
    if (!handler.propsStore.getFieldProps(child).isFiltered) return child;
  }
  return null;
};

export const getNextChild: <
  NodeProps extends Record<string, unknown> = Record<string, unknown>,
  NodeType extends TDataNode<NodeProps> = TDataNode<NodeProps>,
>(
  handler: TreeDataController<NodeProps, NodeType>,
  id: TId,
  avoidChildren?: boolean,
  avoidFiltered?: boolean,
) => TId | null = (handler, id, avoidChildren, avoidFiltered) => {
  const nodeProps = handler.propsStore.getFieldProps(id);
  const fatherProps = handler.propsStore.getFieldProps(nodeProps.parentId);
  let childPosition =
    fatherProps.children?.findIndex((current) => current === nodeProps.id) ??
    Infinity;
  if (!avoidChildren && nodeProps.isExpanded) {
    if (!avoidFiltered && nodeProps.children.length > 0)
      return nodeProps.children[0];

    const firstNonFilteredChild = getFirstNonFilteredChild(
      handler,
      nodeProps.id,
    );
    if (firstNonFilteredChild !== null) return firstNonFilteredChild;
  }
  while (childPosition < fatherProps.children.length - 1) {
    const nextSibling = fatherProps.children[childPosition + 1];
    if (
      !avoidFiltered ||
      !handler.propsStore.getFieldProps(nextSibling).isFiltered
    )
      return nextSibling;
    childPosition++;
  }
  if (nodeProps.parentId === 'root') return null;
  return getNextChild(handler, fatherProps.id, true, avoidFiltered);
};

export const getNextNodeWithKey: <
  NodeProps extends Record<string, unknown> = Record<string, unknown>,
  NodeType extends TDataNode<NodeProps> = TDataNode<NodeProps>,
>(
  handler: TreeDataController<NodeProps, NodeType>,
  id: TId,
  firstKey: string,
  avoidFiltered?: boolean,
) => TId | null = (handler, id, firstKey, avoidFiltered) => {
  let nextChildWithKey: TId | null = id;
  do {
    nextChildWithKey = getNextChild(
      handler,
      nextChildWithKey,
      false,
      avoidFiltered,
    );
    if (nextChildWithKey !== null) {
      const nodeProps = handler.propsStore.getFieldProps(nextChildWithKey);
      if (
        (!nodeProps.isFiltered || !avoidFiltered) &&
        nodeProps.label.toUpperCase().startsWith(firstKey.toUpperCase())
      )
        return nextChildWithKey;
    }
  } while (nextChildWithKey !== null);

  const nodeProps = handler.propsStore.getFieldProps(id);
  const positionInParent = handler.propsStore
    .getFieldProps(nodeProps.parentId)
    .children.findIndex((current) => current === id);

  if (nodeProps.parentId !== 'root' || positionInParent > 0) {
    const [firstChildOfTree] =
      handler.propsStore.getFieldProps('root').children;
    const firstChildProps = handler.propsStore.getFieldProps(firstChildOfTree);

    if (
      firstChildOfTree &&
      (!avoidFiltered || !firstChildProps.isFiltered) &&
      firstChildProps.label.toUpperCase().startsWith(firstKey.toUpperCase())
    )
      return firstChildOfTree;

    nextChildWithKey = firstChildOfTree;
    do {
      nextChildWithKey = getNextChild(
        handler,
        nextChildWithKey,
        false,
        avoidFiltered,
      );
      if (nextChildWithKey) {
        const currentNodeProps =
          handler.propsStore.getFieldProps(nextChildWithKey);
        if (
          (!avoidFiltered || !currentNodeProps.isFiltered) &&
          currentNodeProps.label
            .toUpperCase()
            .startsWith(firstKey.toUpperCase())
        )
          return nextChildWithKey;
      }
    } while (nextChildWithKey !== id && nextChildWithKey !== null);
  }

  return null;
};

export const getNodePath: <
  NodeProps extends Record<string, unknown> = Record<string, unknown>,
  NodeType extends TDataNode<NodeProps> = TDataNode<NodeProps>,
>(
  handler: TreeDataController<NodeProps, NodeType>,
  nodeId: TId,
) => TId[] = (handler, nodeId) => {
  const path: TId[] = [nodeId];
  let currentStep: TId = nodeId;
  do {
    currentStep = handler.propsStore.getFieldProps(currentStep).parentId;
    path.unshift(currentStep);
  } while (currentStep !== 'root');
  return path;
};

export const getCommonAncestor: <
  NodeProps extends Record<string, unknown> = Record<string, unknown>,
  NodeType extends TDataNode<NodeProps> = TDataNode<NodeProps>,
>(
  handler: TreeDataController<NodeProps, NodeType>,
  oneNodeId: TId,
  anotherNodeId: TId,
) => {
  commonAncestor: TId;
  oneNodeAncestorChild: TId;
  anotherNodeAncestorChild: TId;
} | null = (handler, oneNodeId, anotherNodeId) => {
  const oneNodePath = getNodePath(handler, oneNodeId);
  const anotherNodePath = getNodePath(handler, anotherNodeId);

  for (let i = oneNodePath.length - 1; i >= 0; i--) {
    const anotherNodeIndex = anotherNodePath.indexOf(oneNodePath[i]);
    if (anotherNodeIndex !== -1)
      return {
        commonAncestor: oneNodePath[i],
        oneNodeAncestorChild: oneNodePath[i + 1],
        anotherNodeAncestorChild: anotherNodePath[anotherNodeIndex + 1],
      };
  }

  console.warn(
    'This point should have never been reached since the root node is common to all nodes',
    { oneNodeId, anotherNodeId, oneNodePath, anotherNodePath },
  );
  return null;
};

export const unfilterNodes: <
  NodeProps extends Record<string, unknown> = Record<string, unknown>,
  NodeType extends TDataNode<NodeProps> = TDataNode<NodeProps>,
>(
  handler: TreeDataController<NodeProps, NodeType>,
) => void = (handler) => {
  handler.state.filteredNodes.forEach((current) => {
    handler.propsStore.updateField(
      current,
      { isFiltered: false },
      { noEmit: true },
    );
  });

  const firstChild = handler.propsStore.getFieldProps('root').children[0];
  if (firstChild) handler.setFocusedNode(firstChild);
  handler.setState({ filteredNodes: [] });
};

export const matchNodesAgainstString: <
  NodeProps extends Record<string, unknown> = Record<string, unknown>,
  NodeType extends TDataNode<NodeProps> = TDataNode<NodeProps>,
>(
  handler: TreeDataController<NodeProps, NodeType>,
  searchString: string,
  nodeId?: TId,
) => boolean = (handler, searchString, nodeId = 'root') => {
  let hasMatched = false;
  let matchNode: TId | null = null;
  const nodeProps = handler.propsStore.getFieldProps(nodeId);
  if (
    nodeId !== 'root' &&
    nodeProps.label.match(new RegExp(searchString, 'i'))
  ) {
    handler.propsStore.updateField(nodeId, { isFiltered: false });
    hasMatched = true;
    matchNode = nodeProps.id;
    handler.setState({
      filteredNodes: handler.state.filteredNodes.filter(
        (current) => current !== nodeId,
      ),
    });
  }
  let hasChildrenMatched = false;
  nodeProps.children.forEach((current) => {
    hasChildrenMatched =
      matchNodesAgainstString(handler, searchString, current) || hasMatched;
    hasMatched = hasChildrenMatched || hasMatched;
    if (hasMatched && !matchNode) matchNode = current;
  });
  if (hasChildrenMatched) {
    handler.propsStore.updateField(nodeId, { isExpanded: true });
    if (hasMatched && !matchNode) matchNode = nodeId;
  }
  if (!hasMatched) {
    handler.propsStore.updateField(
      nodeId,
      { isFiltered: true },
      { noEmit: true },
    );
    handler.setState({
      filteredNodes: [...handler.state.filteredNodes, nodeId],
    });
  }
  if (matchNode !== null) handler.setFocusedNode(matchNode);
  return hasMatched;
};

export const selectAllNodesBetweenTwoNodes: <
  NodeProps extends Record<string, unknown> = Record<string, unknown>,
  NodeType extends TDataNode<NodeProps> = TDataNode<NodeProps>,
>(
  handler: TreeDataController<NodeProps, NodeType>,
  oneNodeId: TId,
  anotherNodeId: TId,
  avoidFiltered?: boolean,
) => void = (handler, oneNodeId, anotherNodeId, avoidFiltered) => {
  const commonAncestorData = getCommonAncestor(
    handler,
    oneNodeId,
    anotherNodeId,
  );
  if (commonAncestorData) {
    const { anotherNodeAncestorChild, commonAncestor, oneNodeAncestorChild } =
      commonAncestorData;
    const ancestorProps = handler.propsStore.getFieldProps(commonAncestor);
    const oneNodePosition = ancestorProps.children.findIndex(
      (current) => current === oneNodeAncestorChild,
    );
    const anotherNodePosition = ancestorProps.children.findIndex(
      (current) => current === anotherNodeAncestorChild,
    );

    let currentId: TId | null =
      oneNodePosition >= anotherNodePosition ? anotherNodeId : oneNodeId;
    const lastNodeId =
      oneNodePosition < anotherNodePosition ? anotherNodeId : oneNodeId;
    handler.toggleNodeSelectedState(currentId, true);
    do {
      currentId = getNextChild(handler, currentId, avoidFiltered);
      if (currentId !== null) handler.toggleNodeSelectedState(currentId, true);
    } while (currentId !== lastNodeId && currentId !== null);
  }
};
